import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { userService } from '~/services/userAPI';
import { UserActionType } from '~/actions/userActions';
import { RenderMode } from '~/types/common';
import {
  ApiQueryFnData,
  formatErrorPayload,
  UseApiMutationOptions,
  UseApiQueryOptions,
  UseSafeApiQueryResult,
  validateApiResponse,
} from '../utils';

const ROOT_KEY = ['user'];
const getUserQueryKey = () => [...ROOT_KEY];

export const useCurrentUser = <Mode extends RenderMode>({
  login,
  ...options
}: { login?: boolean } & UseApiQueryOptions<typeof userService.getCurrentUser> = {}) => {
  const dispatch = useDispatch();

  return useQuery(
    getUserQueryKey(),
    async () => validateApiResponse(await userService.getCurrentUser(login)).data,
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      onSuccess: (userData) => {
        /**
         * TODO: remove after redux is removed.
         */
        dispatch({
          type: UserActionType.GET_CURRENT_USER_OK,
          data: userData,
        });
      },
      ...options,
    },
  ) as UseSafeApiQueryResult<Mode, typeof userService.getCurrentUser>;
};

export const useUserLogoutMutation = (
  options?: UseApiMutationOptions<typeof userService.postUserLogout>,
) => {
  const dispatch = useDispatch();

  return useMutation(async () => validateApiResponse(await userService.postUserLogout()).data, {
    onError: (error) => dispatch(formatErrorPayload(error)),
    ...options,
  });
};

export const useUserAcceptToSMutation = <T extends typeof userService.acceptAgreements>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(async () => validateApiResponse(await userService.acceptAgreements()).data, {
    onError: (error) => dispatch(formatErrorPayload(error)),
    ...options,
    onSuccess: () => {
      queryClient.invalidateQueries(getUserQueryKey());
    },
  });
};

export const MFA_CONFIGURATION_ERROR = 'MFA_CONFIGURATION_ERROR';

export const useUpdateUserMFAConfigurationMutation = (
  options?: UseApiMutationOptions<typeof userService.postUserLogout>,
) => {
  const dispatch = useDispatch();

  return useMutation(
    async (params: { mfaEnrollment: 'request_enrollment' | 'request_unenrollment' }) =>
      validateApiResponse(await userService.updateUserMFAConfiguration(params)).data,
    {
      onError: (error) =>
        dispatch(
          formatErrorPayload(error, {
            type: MFA_CONFIGURATION_ERROR,
          }),
        ),
      ...options,
    },
  );
};

export const usePatchUserDetailsMutation = <T extends typeof userService.editCurrentUserDetails>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async (data: ApiQueryFnData<T>['details']) => {
      const userData: ApiQueryFnData<T> | undefined = queryClient.getQueryData(getUserQueryKey());

      if (!userData?.details) {
        throw new Error('failed to find user details to patch');
      }

      return validateApiResponse(
        await userService.editCurrentUserDetails({ ...userData.details, ...data }),
      ).data;
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      onSuccess: () => {
        queryClient.invalidateQueries(getUserQueryKey());
      },
      ...options,
    },
  );
};

export const useDeleteUserMutation = <T extends typeof userService.deleteCurrentUser>(
  options?: UseApiMutationOptions<T>,
) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(async () => validateApiResponse(await userService.deleteCurrentUser()).data, {
    onError: (error) => dispatch(formatErrorPayload(error)),
    ...options,
  });
};
